import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Container,
  Typography,
  Skeleton,
  Collapse,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';

import {
  SectionContent,
  PageHeader,
  StyledList,
  InfoCard,
  CardCarousel,
  NewsCarousel,
  LegalCard,
  ProjectSelector
} from '../../components';
import { ContactLink } from '../../components';
import SEO from '../../components/SEO';
import { LIBRARY_CONTENT } from '../../data';
import { seoData } from '../../data/seoData';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import { openPDF } from '../../utils';
import useMarketInsights from '../../hooks/useMarketInsights';
import useStrategyDescriptionCovers from '../../hooks/useStrategyDescriptionCovers';
import useStrategyAssets from '../../hooks/useStrategyAssets';
import monthlyReportsImage from '../../assets/images/library/monthly-reports.jpg';

const factsheetImageDravon =
  'https://www.dropbox.com/scl/fi/if1u1e8g8jm5l4m4ib13w/monthly-factsheet-dravon.png?rlkey=k1ms5c7tfhfqwq1sgouhcxl6a&st=uxn95jvn&raw=1';
const factsheetImageMerin =
  'https://www.dropbox.com/scl/fi/bp901eo70g7t8f60fjqfm/monthly-factsheet-merin.png?rlkey=gs55qjucljukxg81z7ji60mmm&st=ameg8f6p&raw=1';
const factsheetImageStrandQ =
  'https://www.dropbox.com/scl/fi/ydbpadxgl2zjg4yn5a4xq/monthly-factsheet-strandq.png?rlkey=nz79d4ze9c411lljjvxgyr3j2&st=gci4oj4u&raw=1';
const strategyCoverDravon =
  'https://www.dropbox.com/scl/fi/qk11h38t3pdqwiv6vvmcv/202602-StrategyDescription-Dravon-cover.jpg?rlkey=u808aoukmmlwod8szp02xd2qi&st=vjem94e7&raw=1';
const strategyCoverStrandQ =
  'https://www.dropbox.com/scl/fi/e2k5jy63wbpjt47iydmay/StrategyDescription-StrandQ-202602-cover.jpg?rlkey=ktpnjampq04756kdcx3hjsjxv&st=zid9qlzc&raw=1';
const strategyCoverMerin =
  'https://www.dropbox.com/scl/fi/xpqbedoiqcft5q3das4x5/202602-Strategy-Description-Merin-cover.jpg?rlkey=mso60k4aaixppk8ik7tfufp7u&st=bjlf3q07&raw=1';

// Helper function to get approximate month from ISO week number
const getMonthFromWeek = (year, week) => {
  // Approximate: each month has ~4.33 weeks
  // Week 1-4 -> Jan, 5-8 -> Feb, etc.
  const approximateMonth = Math.ceil(week / 4.33);
  return Math.min(approximateMonth, 12);
};

const LibraryPage = () => {
  // Video ref for autoplay control
  const videoRef = useRef(null);
  // Video loading state
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // CMS: weekly insights + monthly reports (replaces static arrays)
  const { weeklyHistory, monthlyHistory, weeklyInsightContent } =
    useMarketInsights();

  // CMS: strategy description covers
  const { covers: strategyCovers } = useStrategyDescriptionCovers();

  // CMS: latest strategy description PDFs (for Library portrait cards)
  const { documents: strandQDocs } = useStrategyAssets('strand-q');
  const { documents: merinDocs } = useStrategyAssets('merin');
  const { documents: dravonDocs } = useStrategyAssets('dravon');

  // History panel toggles for market insights
  const [weeklyHistoryOpen, setWeeklyHistoryOpen] = useState(false);
  const [monthlyHistoryOpen, setMonthlyHistoryOpen] = useState(false);

  // Filters for market insights
  const [weeklyFilterYear, setWeeklyFilterYear] = useState('all');
  const [weeklyFilterMonth, setWeeklyFilterMonth] = useState('all');
  const [monthlyFilterYear, setMonthlyFilterYear] = useState('all');
  const [monthlyFilterMonth, setMonthlyFilterMonth] = useState('all');

  // Extract unique years and months from data
  const weeklyYears = useMemo(() => {
    return [
      'all',
      ...Array.from(new Set(weeklyHistory.map(i => i.year))).sort(
        (a, b) => b - a
      )
    ];
  }, [weeklyHistory]);

  const monthlyYears = useMemo(() => {
    return [
      'all',
      ...Array.from(new Set(monthlyHistory.map(r => r.year))).sort(
        (a, b) => b - a
      )
    ];
  }, [monthlyHistory]);

  const months = [
    { value: 'all', label: 'All Months' },
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Filter weekly insights
  const filteredWeeklyHistory = useMemo(() => {
    return weeklyHistory.filter(item => {
      const yearMatch =
        weeklyFilterYear === 'all' || item.year === weeklyFilterYear;
      const itemMonth = getMonthFromWeek(item.year, item.week);
      const monthMatch =
        weeklyFilterMonth === 'all' || itemMonth === weeklyFilterMonth;
      return yearMatch && monthMatch;
    });
  }, [weeklyHistory, weeklyFilterYear, weeklyFilterMonth]);

  // Filter monthly insights
  const filteredMonthlyHistory = useMemo(() => {
    return monthlyHistory.filter(item => {
      const yearMatch =
        monthlyFilterYear === 'all' || item.year === monthlyFilterYear;
      const monthMatch =
        monthlyFilterMonth === 'all' || item.month === monthlyFilterMonth;
      return yearMatch && monthMatch;
    });
  }, [monthlyHistory, monthlyFilterYear, monthlyFilterMonth]);

  const companyVideoUrl =
    'https://www.dropbox.com/scl/fi/ra9e6lqlf3n1wlqf9o5q9/MarQ-intro.mp4?rlkey=sr7b1m14c3tr5ks1rzkgnl5b6&st=ficeba75&raw=1';

  // Autoplay video when component mounts
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay prevented by browser
      });
    }
  }, []);

  // Project-specific images
  const projectImages = {
    'Strand Q': factsheetImageStrandQ,
    Dravon: factsheetImageDravon,
    Merin: factsheetImageMerin
  };

  // Function to get an appropriate image for a factsheet based on project
  const getFactsheetImage = project => {
    // Use project-specific image if available
    if (projectImages[project]) {
      return projectImages[project];
    }
    // For other projects without specific images, use Strand Q image as fallback
    return factsheetImageStrandQ;
  };

  // State for project filter
  const [selectedProject, setSelectedProject] = useState('Strand Q');
  const projectOptions = [
    { label: 'Strand Q', value: 'Strand Q' },
    { label: 'Merin', value: 'Merin' },
    { label: 'Dravon', value: 'Dravon' }
  ];

  const handleSelectProject = project => {
    setSelectedProject(project);
  };

  // Latest 3 factsheet assets from CMS
  const selectedFactsheetDocs =
    selectedProject === 'Strand Q'
      ? strandQDocs?.allFactsheets || []
      : selectedProject === 'Dravon'
        ? dravonDocs?.allFactsheets || []
        : merinDocs?.allFactsheets || [];

  const latestFactsheetCards = selectedFactsheetDocs.slice(0, 3).map(doc => ({
    TITLE: doc.label,
    DESCRIPTION: `${selectedProject} Program`,
    document: doc,
    imageSrc: getFactsheetImage(selectedProject)
  }));

  // Dynamic insights list items with week label from CMS
  const insightsListItems = [
    {
      TEXT: `${weeklyInsightContent?.label || 'Week'} - Market Insights, offering concise commentary on relevant market movements, macroeconomic developments, and tactical positioning.`
    },
    {
      TEXT: 'Monthly Market Insight, which deliver a broader analysis of current global economic and trends in FX, WTI, Gold, US and EU stock markets.'
    }
  ];

  return (
    <>
      <SEO
        title={seoData.library.title}
        content={seoData.library.content}
        keywords={seoData.library.keywords}
        canonical={seoData.library.canonical}
        ogImage={seoData.library.ogImage}
        ogType={seoData.library.ogType}
        structuredData={seoData.library.structuredData}
      />
      {/* Company Video Section */}
      <Box
        id='hero'
        sx={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          mb: SECTION_SPACING,
          mt: 2,
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {/* Skeleton loader while video is loading */}
        {!isVideoLoaded && (
          <Skeleton
            variant='rectangular'
            animation='wave'
            sx={{
              width: '100%',
              height: { xs: '200px', sm: '350px', md: '500px' },
              borderRadius: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }}
          />
        )}

        <video
          ref={videoRef}
          controls
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setIsVideoLoaded(true)}
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '600px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            display: isVideoLoaded ? 'block' : 'none'
          }}
        >
          <source src={companyVideoUrl} type='video/mp4' />
          Your browser does not support the video tag.
        </video>
      </Box>

      {/* Main "Library" section */}
      <PageHeader
        title={LIBRARY_CONTENT.TITLE}
        subtitle={LIBRARY_CONTENT.SUBTITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />

      {/* SECTION 1: Stay Up-to-Date */}
      <SectionContent
        id='stayUpdated'
        title={LIBRARY_CONTENT.SECTIONS.STAY_UP_TO_DATE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: LIBRARY_CONTENT.SECTIONS.STAY_UP_TO_DATE.TEXT[0],
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <NewsCarousel
          newsItems={LIBRARY_CONTENT.SECTIONS.STAY_UP_TO_DATE.NEWS_UPDATES}
        />
      </Box>

      <SectionContent
        initialSpacing={false}
        text={[
          {
            text: LIBRARY_CONTENT.SECTIONS.STAY_UP_TO_DATE.HIGHLIGHT,
            textAlign: 'center',
            padding: `${THEME.SPACING.XL} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            marginBottom: SECTION_SPACING,
            marginTop: SUBSECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* SECTION 2: Strategy Description */}
      <SectionContent
        id='factsheetDescription'
        title={LIBRARY_CONTENT.SECTIONS.FACTSHEET_DESCRIPTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          ...LIBRARY_CONTENT.SECTIONS.FACTSHEET_DESCRIPTION.TEXT.map(
            (text, index, array) => ({
              text,
              marginBottom:
                index === array.length - 1
                  ? SUBSECTION_SPACING
                  : ELEMENT_SPACING,
              fontSize: '1.3rem'
            })
          )
        ]}
      />

      {/* Strategy Description Portrait Cards */}
      <Container maxWidth='xl' sx={{ mb: SECTION_SPACING }}>
        <CardCarousel
          items={LIBRARY_CONTENT.SECTIONS.FACTSHEET_DESCRIPTION.STRATEGY_FACTSHEETS.map(
            item => {
              const strategySlug =
                item.TITLE === 'Strand Q'
                  ? 'strand-q'
                  : item.TITLE === 'Dravon'
                    ? 'dravon'
                    : 'merin';

              const descriptionDoc =
                item.TITLE === 'Strand Q'
                  ? strandQDocs?.description
                  : item.TITLE === 'Dravon'
                    ? dravonDocs?.description
                    : merinDocs?.description;

              return {
                ...item,
                descriptionDoc,
                imageSrc:
                  strategyCovers?.[strategySlug]?.url ??
                  (item.TITLE === 'Strand Q'
                    ? strategyCoverStrandQ
                    : item.TITLE === 'Dravon'
                      ? strategyCoverDravon
                      : strategyCoverMerin)
              };
            }
          )}
          expectedMaxCardHeightPx={420}
          spaceForDotsPx={30}
          renderCard={(item, index, isActive) => (
            <InfoCard
              key={`strategy-factsheet-${index}`}
              imageSrc={item.imageSrc}
              onClick={() =>
                item.descriptionDoc ? openPDF(item.descriptionDoc) : null
              }
              variant='elevated'
              hideContent={true}
              sx={{
                height: 'auto',
                opacity: isActive ? 1 : 0.8,
                transition: 'all 0.3s ease',
                minWidth: 220,
                maxWidth: 320,
                padding: 0
              }}
              imageSx={{ objectFit: 'cover', height: 450, width: '100%' }}
            />
          )}
        />
      </Container>

      {/* SECTION 3: Factsheets */}
      <SectionContent
        id='factsheets'
        title={LIBRARY_CONTENT.SECTIONS.FACTSHEETS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: LIBRARY_CONTENT.SECTIONS.FACTSHEETS.TEXT[0],
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Factsheets list with embedded content */}
      <Box
        sx={{
          width: '100%',
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          },
          mb: ELEMENT_SPACING
        }}
      >
        <StyledList
          variant='blue'
          items={LIBRARY_CONTENT.SECTIONS.FACTSHEETS.LIST_ITEMS}
          additionalContent={{
            0: (
              <Box>
                {/* Project Selector Button */}
                <Box sx={{ mb: SUBSECTION_SPACING }}>
                  <ProjectSelector
                    selectedProject={selectedProject}
                    projectOptions={projectOptions}
                    onSelectProject={handleSelectProject}
                  />
                </Box>

                {/* Latest Factsheet Carousel */}
                <CardCarousel
                  compact={true}
                  items={latestFactsheetCards}
                  expectedMaxCardHeightPx={310}
                  emptyMessage={`No factsheet available for ${selectedProject} yet. Check back later.`}
                  renderCard={(item, index, isActive) => (
                    <InfoCard
                      key={`factsheet-${index}`}
                      title={item.TITLE}
                      description={item.DESCRIPTION}
                      imageSrc={item.imageSrc}
                      onClick={() => openPDF(item.document)}
                      variant='elevated'
                      sx={{
                        height: 'auto',
                        opacity: isActive ? 1 : 0.8,
                        transition: 'all 0.3s ease'
                      }}
                      imageSx={{ objectFit: 'cover', height: 160 }}
                    />
                  )}
                />
              </Box>
            )
          }}
        />
      </Box>

      {/* SECTION 4: Insights */}
      <SectionContent
        id='insights'
        title={LIBRARY_CONTENT.SECTIONS.INSIGHTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: LIBRARY_CONTENT.SECTIONS.INSIGHTS.TEXT[0],
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Insights list with embedded content */}
      <Box
        sx={{
          width: '100%',
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          },
          mb: ELEMENT_SPACING
        }}
      >
        <StyledList
          variant='blue'
          items={insightsListItems}
          additionalContent={{
            0: (
              <Box>
                {/* Weekly Market Insights - Legal Cards Grid */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {weeklyInsightContent?.items?.map((insight, index) => (
                    <Grid item xs={12} sm={6} md={3} key={`insight-${index}`}>
                      <LegalCard
                        title={insight.title}
                        description={insight.description}
                        hasModal={true}
                        modalTitle={`${insight.title} - Weekly Market Insight`}
                        modalContent={insight.content}
                        modalImage={insight.imageUrl}
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* Weekly Market Insights PDF Carousel */}
                <CardCarousel
                  compact={true}
                  items={weeklyHistory.slice(0, 3).map(insight => ({
                    TITLE: 'Market Insight',
                    DESCRIPTION: insight.label,
                    document: insight,
                    imageSrc: require('../../assets/images/library/weekly-marketinsight.jpeg')
                  }))}
                  expectedMaxCardHeightPx={100}
                  renderCard={(item, index, isActive) => (
                    <InfoCard
                      key={`marketinsight-${index}`}
                      title={item.TITLE}
                      description={item.DESCRIPTION}
                      imageSrc={item.imageSrc}
                      onClick={() => openPDF(item.document)}
                      variant='elevated'
                      sx={{
                        height: 'auto',
                        opacity: isActive ? 1 : 0.8,
                        transition: 'all 0.3s ease'
                      }}
                      imageSx={{ objectFit: 'cover', height: 137 }}
                    />
                  )}
                />

                {/* Weekly history accordion */}
                {weeklyHistory.length > 3 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size='small'
                      variant='text'
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: weeklyHistoryOpen
                              ? 'rotate(180deg)'
                              : 'none',
                            transition: 'transform 0.2s'
                          }}
                        />
                      }
                      onClick={() => setWeeklyHistoryOpen(o => !o)}
                      sx={{
                        color: THEME.COLORS.PRIMARY_BLUE,
                        textTransform: 'none',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                        fontSize: '0.85rem',
                        px: 0,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {weeklyHistoryOpen
                        ? 'Hide past issues'
                        : `View all ${weeklyHistory.length} issues`}
                    </Button>
                    <Collapse in={weeklyHistoryOpen}>
                      <Box sx={{ mt: 1.5, pb: 0.5 }}>
                        {/* Weekly Insights Filters */}
                        <Box
                          sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <FilterListIcon
                              sx={{
                                fontSize: '1.1rem',
                                color: THEME.COLORS.NEUTRAL_GRAY
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                                color: THEME.COLORS.NEUTRAL_GRAY
                              }}
                            >
                              Filter:
                            </Typography>
                          </Box>
                          <FormControl size='small' sx={{ minWidth: 120 }}>
                            <Select
                              value={weeklyFilterYear}
                              onChange={e =>
                                setWeeklyFilterYear(e.target.value)
                              }
                              displayEmpty
                              sx={{
                                fontSize: '0.85rem',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE + '40'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  { borderColor: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              {weeklyYears.map(year => (
                                <MenuItem
                                  key={year}
                                  value={year}
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  {year === 'all' ? 'All Years' : year}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size='small' sx={{ minWidth: 140 }}>
                            <Select
                              value={weeklyFilterMonth}
                              onChange={e =>
                                setWeeklyFilterMonth(e.target.value)
                              }
                              displayEmpty
                              sx={{
                                fontSize: '0.85rem',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE + '40'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  { borderColor: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              {months.map(month => (
                                <MenuItem
                                  key={month.value}
                                  value={month.value}
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  {month.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {(weeklyFilterYear !== 'all' ||
                            weeklyFilterMonth !== 'all') && (
                            <Button
                              size='small'
                              onClick={() => {
                                setWeeklyFilterYear('all');
                                setWeeklyFilterMonth('all');
                              }}
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                color: THEME.COLORS.NEUTRAL_GRAY,
                                '&:hover': { color: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              Clear filters
                            </Button>
                          )}
                        </Box>

                        {Array.from(
                          new Set(filteredWeeklyHistory.map(i => i.year))
                        )
                          .sort((a, b) => b - a)
                          .map(year => (
                            <Box key={year} sx={{ mb: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.78rem',
                                  fontWeight:
                                    THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                                  color: THEME.COLORS.NEUTRAL_GRAY,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.06em',
                                  mb: 0.75
                                }}
                              >
                                {year}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75
                                }}
                              >
                                {filteredWeeklyHistory
                                  .filter(i => i.year === year)
                                  .map(item => (
                                    <Chip
                                      key={item.name}
                                      label={item.label}
                                      size='small'
                                      onClick={() => openPDF(item)}
                                      sx={{
                                        borderRadius: '6px',
                                        fontWeight:
                                          THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        backgroundColor: '#e8f0ff',
                                        color: THEME.COLORS.PRIMARY_BLUE,
                                        '&:hover': {
                                          backgroundColor:
                                            THEME.COLORS.PRIMARY_BLUE + '20'
                                        }
                                      }}
                                    />
                                  ))}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>
            ),
            1: (
              <Box>
                {/* Monthly Market Insights Carousel */}
                <CardCarousel
                  compact={true}
                  items={monthlyHistory.slice(0, 3).map(report => ({
                    TITLE: 'Market Insight',
                    DESCRIPTION: report.label,
                    document: report,
                    imageSrc: monthlyReportsImage
                  }))}
                  expectedMaxCardHeightPx={200}
                  renderCard={(item, index, isActive) => (
                    <InfoCard
                      key={`monthly-insight-${index}`}
                      title={item.TITLE}
                      description={item.DESCRIPTION}
                      imageSrc={item.imageSrc}
                      onClick={() => openPDF(item.document)}
                      variant='elevated'
                      sx={{
                        height: 'auto',
                        opacity: isActive ? 1 : 0.8,
                        transition: 'all 0.3s ease'
                      }}
                      imageSx={{ objectFit: 'cover', height: 137 }}
                    />
                  )}
                />

                {/* Monthly history accordion */}
                {monthlyHistory.length > 3 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size='small'
                      variant='text'
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: monthlyHistoryOpen
                              ? 'rotate(180deg)'
                              : 'none',
                            transition: 'transform 0.2s'
                          }}
                        />
                      }
                      onClick={() => setMonthlyHistoryOpen(o => !o)}
                      sx={{
                        color: THEME.COLORS.PRIMARY_BLUE,
                        textTransform: 'none',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                        fontSize: '0.85rem',
                        px: 0,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {monthlyHistoryOpen
                        ? 'Hide past reports'
                        : `View all ${monthlyHistory.length} reports`}
                    </Button>
                    <Collapse in={monthlyHistoryOpen}>
                      <Box sx={{ mt: 1.5, pb: 0.5 }}>
                        {/* Monthly Insights Filters */}
                        <Box
                          sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <FilterListIcon
                              sx={{
                                fontSize: '1.1rem',
                                color: THEME.COLORS.NEUTRAL_GRAY
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                                color: THEME.COLORS.NEUTRAL_GRAY
                              }}
                            >
                              Filter:
                            </Typography>
                          </Box>
                          <FormControl size='small' sx={{ minWidth: 120 }}>
                            <Select
                              value={monthlyFilterYear}
                              onChange={e =>
                                setMonthlyFilterYear(e.target.value)
                              }
                              displayEmpty
                              sx={{
                                fontSize: '0.85rem',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE + '40'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  { borderColor: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              {monthlyYears.map(year => (
                                <MenuItem
                                  key={year}
                                  value={year}
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  {year === 'all' ? 'All Years' : year}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size='small' sx={{ minWidth: 140 }}>
                            <Select
                              value={monthlyFilterMonth}
                              onChange={e =>
                                setMonthlyFilterMonth(e.target.value)
                              }
                              displayEmpty
                              sx={{
                                fontSize: '0.85rem',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE + '40'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME.COLORS.PRIMARY_BLUE
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  { borderColor: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              {months.map(month => (
                                <MenuItem
                                  key={month.value}
                                  value={month.value}
                                  sx={{ fontSize: '0.85rem' }}
                                >
                                  {month.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {(monthlyFilterYear !== 'all' ||
                            monthlyFilterMonth !== 'all') && (
                            <Button
                              size='small'
                              onClick={() => {
                                setMonthlyFilterYear('all');
                                setMonthlyFilterMonth('all');
                              }}
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                color: THEME.COLORS.NEUTRAL_GRAY,
                                '&:hover': { color: THEME.COLORS.PRIMARY_BLUE }
                              }}
                            >
                              Clear filters
                            </Button>
                          )}
                        </Box>

                        {Array.from(
                          new Set(filteredMonthlyHistory.map(r => r.year))
                        )
                          .sort((a, b) => b - a)
                          .map(year => (
                            <Box key={year} sx={{ mb: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.78rem',
                                  fontWeight:
                                    THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                                  color: THEME.COLORS.NEUTRAL_GRAY,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.06em',
                                  mb: 0.75
                                }}
                              >
                                {year}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75
                                }}
                              >
                                {filteredMonthlyHistory
                                  .filter(r => r.year === year)
                                  .map(item => (
                                    <Chip
                                      key={item.name}
                                      label={item.label}
                                      size='small'
                                      onClick={() => openPDF(item)}
                                      sx={{
                                        borderRadius: '6px',
                                        fontWeight:
                                          THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        backgroundColor: '#e8f0ff',
                                        color: THEME.COLORS.PRIMARY_BLUE,
                                        '&:hover': {
                                          backgroundColor:
                                            THEME.COLORS.PRIMARY_BLUE + '20'
                                        }
                                      }}
                                    />
                                  ))}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>
            )
          }}
        />
      </Box>

      <SectionContent
        initialSpacing={false}
        text={[
          {
            text: LIBRARY_CONTENT.SECTIONS.INSIGHTS.HIGHLIGHT,
            textAlign: 'center',
            padding: `${THEME.SPACING.XL} ${THEME.SPACING.XL}`,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {LIBRARY_CONTENT.FOOTER_NOTE}
        </Typography>
        <Box mt={ELEMENT_SPACING}>
          <Typography
            variant='body2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.2rem',
              color: THEME.COLORS.PRIMARY_BLUE,
              lineHeight: 1.6
            }}
          >
            Talk to an Expert –&nbsp;
            <ContactLink
              style={{
                color: THEME.COLORS.NEUTRAL_GRAY,
                textDecoration: 'underline',
                fontSize: '1rem'
              }}
            >
              Schedule Your meeting
            </ContactLink>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LibraryPage;
